import { ClusteringRunResponse, LatLongTuple, ManualCluster } from '~/components/LERE/types';

export type NoisePolicy = 'nearest_cluster' | 'leave_noise';

export type ManualFocus = {
  center: LatLongTuple;
  zoom: number;
};

export type ClusterMapState = {
  selectedClusterId: number | null;
  selectedRunId: string;
  showRunParameters: boolean;
  previewData: ClusteringRunResponse | null;
  isEditing: boolean;
  cutKm: number;
  minClusterSize: number;
  minSamples: number;
  maxAssignDistKm: number;
  noisePolicy: NoisePolicy;
  opacity: number;
  maxClusters: number;
  minClusterOffers: number;
  showBorderBreakingClusters: boolean;
  manualClustersDraft: Array<ManualCluster>;
  manualFocus: ManualFocus | null;
  isPickingManualBounds: boolean;
  manualPickPoints: Array<LatLongTuple>;
  newManualCluster: ManualCluster;
};

export type ResetParamsPayload = {
  cutKm: number;
  minClusterSize: number;
  minSamples: number;
  maxAssignDistKm: number;
  noisePolicy: NoisePolicy;
  manualClustersDraft: Array<ManualCluster>;
};

export type ClusterMapAction =
  | { type: 'MERGE_STATE'; payload: Partial<ClusterMapState> }
  | { type: 'RESET_PARAMS'; payload: ResetParamsPayload }
  | { type: 'UPDATE_NEW_MANUAL_CLUSTER'; payload: Partial<ManualCluster> }
  | { type: 'RESET_NEW_MANUAL_CLUSTER' }
  | { type: 'ADD_MANUAL_CLUSTER'; payload: ManualCluster }
  | { type: 'TOGGLE_MANUAL_CLUSTER_ENABLED'; payload: number }
  | { type: 'TOGGLE_MANUAL_CLUSTER_PROTECTED'; payload: number }
  | { type: 'SET_MANUAL_PICK_POINTS'; payload: Array<LatLongTuple> };

const DEFAULT_NEW_MANUAL_CLUSTER: ManualCluster = {
  name: '',
  polygon: [],
  enabled: true,
  protected: true,
};

export const initialClusterMapState: ClusterMapState = {
  selectedClusterId: null,
  selectedRunId: '',
  showRunParameters: false,
  previewData: null,
  isEditing: false,
  cutKm: 90,
  minClusterSize: 400,
  minSamples: 3,
  maxAssignDistKm: 0,
  noisePolicy: 'nearest_cluster',
  opacity: 0.5,
  maxClusters: 0,
  minClusterOffers: 0,
  showBorderBreakingClusters: false,
  manualClustersDraft: [],
  manualFocus: null,
  isPickingManualBounds: false,
  manualPickPoints: [],
  newManualCluster: DEFAULT_NEW_MANUAL_CLUSTER,
};

export function clusterMapReducer(state: ClusterMapState, action: ClusterMapAction): ClusterMapState {
  switch (action.type) {
    case 'MERGE_STATE':
      return { ...state, ...action.payload };
    case 'RESET_PARAMS':
      return {
        ...state,
        cutKm: action.payload.cutKm,
        minClusterSize: action.payload.minClusterSize,
        minSamples: action.payload.minSamples,
        maxAssignDistKm: action.payload.maxAssignDistKm,
        noisePolicy: action.payload.noisePolicy,
        manualClustersDraft: action.payload.manualClustersDraft,
      };
    case 'UPDATE_NEW_MANUAL_CLUSTER':
      return { ...state, newManualCluster: { ...state.newManualCluster, ...action.payload } };
    case 'RESET_NEW_MANUAL_CLUSTER':
      return { ...state, newManualCluster: DEFAULT_NEW_MANUAL_CLUSTER };
    case 'ADD_MANUAL_CLUSTER':
      return { ...state, manualClustersDraft: [...state.manualClustersDraft, action.payload] };
    case 'TOGGLE_MANUAL_CLUSTER_ENABLED':
      return {
        ...state,
        manualClustersDraft: state.manualClustersDraft.map((cluster, idx) =>
          idx === action.payload ? { ...cluster, enabled: !(cluster.enabled ?? true) } : cluster,
        ),
      };
    case 'TOGGLE_MANUAL_CLUSTER_PROTECTED':
      return {
        ...state,
        manualClustersDraft: state.manualClustersDraft.map((cluster, idx) =>
          idx === action.payload ? { ...cluster, protected: !(cluster.protected ?? true) } : cluster,
        ),
      };
    case 'SET_MANUAL_PICK_POINTS':
      return { ...state, manualPickPoints: action.payload };
    default:
      return state;
  }
}
